import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { useMarketplaceFetchApi } from './marketplaceFetch';

interface FavoritesContextValue {
  /** Set of "artifactType:artifactName" keys that the current user has favorited. */
  favorites: Set<string>;
  /** True while the initial favorites list is loading. */
  loading: boolean;
  /** Toggle a favorite: adds if not present, removes if already favorited. */
  toggleFavorite: (artifactName: string, artifactType: string) => Promise<void>;
  /** Force-refresh the favorites list from the backend. */
  refresh: () => void;
}

/** Build a stable key for de-duplication. */
export function favKey(artifactName: string, artifactType: string): string {
  return `${artifactType}:${artifactName}`;
}

const FavoritesContext = createContext<FavoritesContextValue>({
  favorites: new Set(),
  loading: false,
  toggleFavorite: async () => {},
  refresh: () => {},
});

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();

  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  // Fetch favorites on mount and on refresh
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/favorites`);
        if (resp.ok && !cancelled) {
          const data = await resp.json();
          const set = new Set<string>();
          for (const row of data.favorites ?? []) {
            set.add(favKey(row.artifact_name, row.artifact_type));
          }
          setFavorites(set);
        }
      } catch {
        // Silently fail — user can still browse without favorites
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi, tick]);

  const toggleFavorite = useCallback(
    async (artifactName: string, artifactType: string) => {
      const key = favKey(artifactName, artifactType);
      const isFav = favorites.has(key);

      // Optimistic update
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.delete(key);
        else next.add(key);
        return next;
      });

      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/favorites`, {
          method: isFav ? 'DELETE' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ artifactName, artifactType }),
        });
        if (!resp.ok) {
          throw new Error(`Server returned ${resp.status}`);
        }
      } catch {
        // Revert on failure
        setFavorites(prev => {
          const next = new Set(prev);
          if (isFav) next.add(key);
          else next.delete(key);
          return next;
        });
      }
    },
    [favorites, discoveryApi, fetchApi],
  );

  return (
    <FavoritesContext.Provider value={{ favorites, loading, toggleFavorite, refresh }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
